import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  CHALLENGE_SECONDS,
  LETTERS,
  challengeFor,
  dailyChallengeId,
  newChallengeId,
  newOddChallengeId,
  maskOf,
  pointsForCorrect,
  stageTimeline,
  validChallengeId,
  type Phase,
  type RoundPublic,
} from '@listen-draw/shared';
import { Canvas } from '../components/Canvas.js';
import { Choices } from '../components/Choices.js';
import { ShareResult } from '../components/ShareResult.js';
import { Explanation } from '../components/Explanation.js';
import { AudioSpeed } from '../components/AudioSpeed.js';
import { storage } from '../lib/api.js';
import {
  readAudioSpeed,
  SOLO_AUDIO_SPEED_KEY,
  type AudioSpeed as AudioSpeedValue,
} from '../lib/audio-speed.js';
import { t, useLocale } from '../lib/i18n.js';
import { Player } from '../lib/tts.js';
import { playRecording, prepareRecordings, SOUND_CHECK, unlockAudio } from '../lib/sound.js';
import { track } from '../lib/analytics.js';

type SoloPhase = 'ready' | 'loading' | 'listening' | 'drawing' | 'reveal' | 'paused';
interface Result {
  correct: boolean;
  pct: number | null;
  points: number;
  letter: number | null;
}
const SEEN_KEY = 'ld.seen.oo1';
function seenFamilies(): string[] {
  try {
    const saved: unknown = JSON.parse(storage.get(SEEN_KEY) ?? '[]');
    return Array.isArray(saved)
      ? saved.filter((v): v is string => typeof v === 'string').slice(-199)
      : [];
  } catch {
    return [];
  }
}
function newId(odd = false): string {
  return (odd ? newOddChallengeId : newChallengeId)(
    new Set(seenFamilies()),
    () => crypto.getRandomValues(new Uint32Array(1))[0]! / 0x100000000,
  );
}

export function Solo({ daily = false, odd = false }: { daily?: boolean; odd?: boolean }) {
  useLocale();
  const location = useLocation();
  const nav = useNavigate();
  const candidate = new URLSearchParams(location.search).get('c');
  const randomId = useMemo(() => newId(odd), [odd]);
  const id = daily ? dailyChallengeId() : (candidate ?? randomId);
  const oddMode = odd || id.startsWith('v2-');
  if (!validChallengeId(id))
    return (
      <main className="page solo-page">
        <h1>{t('This challenge link is incomplete.', '挑戦状のリンクが正しくありません。')}</h1>
        <p>{t('Start a fresh round to keep playing.', '新しいラウンドで遊べます。')}</p>
        <Link className="btn btn--primary" to={odd ? '/odd' : '/solo'}>
          {t('Try a round', '一人で遊ぶ')}
        </Link>
      </main>
    );
  return (
    <SoloRound
      key={id}
      id={id}
      daily={daily}
      shared={!!candidate}
      oddMode={oddMode}
      legacy={id.startsWith('v1-')}
      onNext={() => nav(`${oddMode ? '/odd' : '/solo'}?c=${newId(oddMode)}`)}
    />
  );
}

function SoloRound({
  id,
  daily,
  shared,
  oddMode,
  legacy,
  onNext,
}: {
  id: string;
  daily: boolean;
  shared: boolean;
  oddMode: boolean;
  legacy: boolean;
  onNext: () => void;
}) {
  useLocale();
  const challenge = useMemo(() => challengeFor(id), [id]);
  const [firstResult] = useState<Result | null>(() => {
    try {
      const saved = JSON.parse(storage.get(`ld.result.${id}`) ?? 'null') as Result | null;
      return saved &&
        typeof saved.correct === 'boolean' &&
        typeof saved.points === 'number' &&
        [0, -100, 100, 200, 300].includes(saved.points) &&
        (saved.pct === null ||
          (typeof saved.pct === 'number' && saved.pct >= 0 && saved.pct <= 100))
        ? saved
        : null;
    } catch {
      return null;
    }
  });
  const [phase, setPhase] = useState<SoloPhase>('ready');
  const [round, setRound] = useState<RoundPublic | null>(null);
  const [playing, setPlaying] = useState<number | null>(null);
  const [pct, setPct] = useState(0);
  const [result, setResult] = useState<Result | null>(null);
  const [error, setError] = useState('');
  const [soundReady, setSoundReady] = useState(false);
  const [testing, setTesting] = useState(false);
  const [audioSpeed, setAudioSpeed] = useState<AudioSpeedValue>(() =>
    readAudioSpeed(storage.get(SOLO_AUDIO_SPEED_KEY)),
  );
  const [practice, setPractice] = useState(storage.get(`ld.attempt.${id}`) !== null);
  const player = useRef<Player | null>(null);
  const soundTest = useRef<AbortController | null>(null);
  const answered = useRef(false);
  const activeRun = useRef(0);
  const startedAt = useRef(0);
  const phaseRef = useRef(phase);
  phaseRef.current = phase;
  const mode = daily ? 'daily' : 'solo';

  const changeAudioSpeed = (speed: AudioSpeedValue) => {
    player.current?.stop();
    soundTest.current?.abort();
    setTesting(false);
    setAudioSpeed(speed);
    storage.set(SOLO_AUDIO_SPEED_KEY, String(speed));
  };

  useEffect(
    () => () => {
      activeRun.current++;
      player.current?.stop();
      soundTest.current?.abort();
    },
    [],
  );

  const finish = useCallback(
    (letter: number | null) => {
      if (answered.current || phaseRef.current !== 'drawing') return;
      answered.current = true;
      const progress = Math.min(
        100,
        Math.max(0, Math.round((Date.now() - startedAt.current) / (CHALLENGE_SECONDS * 10))),
      );
      const timedOut =
        letter === null || Date.now() >= startedAt.current + CHALLENGE_SECONDS * 1000;
      const correct = !timedOut && letter === challenge.correctLetterIndex;
      const next: Result = {
        correct,
        pct: timedOut ? null : progress,
        points: correct ? pointsForCorrect(progress) : timedOut ? 0 : -100,
        letter: timedOut ? null : letter,
      };
      setResult(next);
      setPct(progress);
      setPhase('reveal');
      setRound((previous) =>
        previous
          ? {
              ...previous,
              answeredCount: timedOut ? 0 : 1,
              reveal: {
                texts: challenge.texts,
                correctLetterIndex: challenge.correctLetterIndex,
                axes: challenge.set.axes,
                keyWords: Object.values(
                  challenge.set.sentences[challenge.order[challenge.correctLetterIndex]!]!.features,
                ),
                scene: challenge.set.scene,
                outcomes: [],
                distribution: [0, 0, 0, 0],
                winnerPlayerId: null,
              },
            }
          : previous,
      );
      if (!practice) storage.set(`ld.result.${id}`, JSON.stringify(next));
      track(
        'round_complete',
        { mode, challenge: id, correct, ...(next.pct !== null ? { pct: next.pct } : {}) },
        `${id}:${activeRun.current}`,
      );
    },
    [challenge, id, mode, practice],
  );

  useEffect(() => {
    if (phase !== 'drawing') return;
    const timer = window.setInterval(() => {
      const value = (Date.now() - startedAt.current) / (CHALLENGE_SECONDS * 10);
      setPct(Math.max(0, Math.min(100, Math.round(value))));
      if (value >= 100) finish(null);
    }, 40);
    const key = (event: KeyboardEvent) => {
      if (
        event.metaKey ||
        event.ctrlKey ||
        event.altKey ||
        event.repeat ||
        /INPUT|TEXTAREA|SELECT/.test((event.target as HTMLElement).tagName)
      )
        return;
      const index = LETTERS.indexOf(event.key.toUpperCase() as (typeof LETTERS)[number]);
      if (index >= 0) {
        event.preventDefault();
        finish(index);
      }
    };
    window.addEventListener('keydown', key);
    return () => {
      clearInterval(timer);
      window.removeEventListener('keydown', key);
    };
  }, [phase, finish]);

  useEffect(() => {
    const hide = () => {
      if (!document.hidden || !['loading', 'listening', 'drawing'].includes(phaseRef.current))
        return;
      activeRun.current++;
      player.current?.stop();
      setRound((previous) =>
        previous?.drawing
          ? { ...previous, drawing: { ...previous.drawing, pausedAt: Date.now() } }
          : previous,
      );
      setPhase('paused');
      setPractice(true);
    };
    document.addEventListener('visibilitychange', hide);
    return () => document.removeEventListener('visibilitychange', hide);
  }, []);

  const soundCheck = async () => {
    soundTest.current?.abort();
    const controller = new AbortController();
    soundTest.current = controller;
    setTesting(true);
    setError('');
    try {
      await unlockAudio();
      await playRecording(SOUND_CHECK, audioSpeed, controller.signal);
      setSoundReady(true);
    } catch {
      if (!controller.signal.aborted)
        setError(
          t(
            'We couldn’t play the sound. Check your volume and connection, then try again.',
            '音声を再生できませんでした。音量と接続を確認して、もう一度お試しください。',
          ),
        );
    } finally {
      if (!controller.signal.aborted) setTesting(false);
    }
  };

  const start = async () => {
    if (
      phaseRef.current === 'loading' ||
      phaseRef.current === 'listening' ||
      phaseRef.current === 'drawing'
    )
      return;
    const run = ++activeRun.current;
    soundTest.current?.abort();
    player.current?.stop();
    setTesting(false);
    setError('');
    setResult(null);
    setPct(0);
    setPlaying(null);
    setPhase('loading');
    answered.current = false;
    setPractice(storage.get(`ld.attempt.${id}`) !== null);
    try {
      await unlockAudio();
      await prepareRecordings([...LETTERS.map((l) => `${l}.`), ...challenge.texts]);
      if (run !== activeRun.current) return;
      if (challenge.set.family) {
        const seen = seenFamilies().filter((family) => family !== challenge.set.family);
        storage.set(SEEN_KEY, JSON.stringify([...seen, challenge.set.family].slice(-199)));
      }
      setSoundReady(true);
      setRound({
        index: 1,
        masks: challenge.texts.map((text) => maskOf(text)),
        audio: { replays: 0, halved: false, done: false },
        drawingReady: true,
        drawingSource: 'pack',
        answeredCount: 0,
      });
      setPhase('listening');
      storage.set(`ld.attempt.${id}`, String(Date.now()));
      // Keep local practice markers bounded; they are not sent to the server.
      try {
        const keys = Object.keys(localStorage).filter((key) => key.startsWith('ld.attempt.'));
        if (keys.length > 120)
          keys
            .sort((a, b) => Number(localStorage.getItem(a)) - Number(localStorage.getItem(b)))
            .slice(0, keys.length - 120)
            .forEach((key) => {
              storage.remove(key);
              storage.remove(key.replace('ld.attempt.', 'ld.result.'));
            });
      } catch {
        /* storage can be unavailable */
      }
      track('demo_start', { mode, challenge: id }, `${id}:${run}`);
      const audio = new Player({
        items: challenge.texts.map((text, letterIndex) => ({ text, letterIndex })),
        rate: audioSpeed,
        gapMs: 700,
        speak: true,
        onItemStart: setPlaying,
        onDone: () => {
          if (run !== activeRun.current) return;
          startedAt.current = Date.now();
          setPlaying(null);
          setRound((previous) =>
            previous
              ? {
                  ...previous,
                  audio: { ...previous.audio, done: true },
                  drawing: {
                    totalStages: 4,
                    durationMs: CHALLENGE_SECONDS * 1000,
                    startAt: startedAt.current,
                    pausedAt: null,
                    pausedTotalMs: 0,
                    stages: stageTimeline(challenge.drawing.strokes, CHALLENGE_SECONDS * 1000),
                  },
                }
              : previous,
          );
          setPhase('drawing');
        },
        onError: () => {
          if (run !== activeRun.current) return;
          setPhase('ready');
          setError(
            t(
              'Sound was interrupted. Check your connection, then restart this round.',
              '音声が途切れました。接続を確認して、このラウンドをやり直してください。',
            ),
          );
          track('audio_error', { mode, challenge: id });
        },
      });
      player.current = audio;
      audio.start();
    } catch {
      if (run !== activeRun.current) return;
      setPhase('ready');
      setError(
        t(
          'We couldn’t load the audio. Check your connection and tap Start again.',
          '音声を読み込めませんでした。接続を確認して、もう一度開始してください。',
        ),
      );
      track('audio_error', { mode, challenge: id });
    }
  };

  const canvasPhase: Phase =
    phase === 'reveal'
      ? 'reveal'
      : phase === 'drawing' || phase === 'paused'
        ? 'drawing'
        : 'listening';
  return (
    <main
      className={`page solo-page${phase === 'listening' || phase === 'drawing' ? ' is-playing' : ''}`}
    >
      <div className="solo-heading">
        <p className="eyebrow">
          {daily
            ? t('The daily listen', '今日の一問')
            : oddMode
              ? t('The odd archive', 'ちょっと変わった問題集')
            : shared
              ? t('Challenge accepted?', '挑戦状が届きました')
              : t('One round. Trust your ears.', '一問だけ、耳で勝負。')}
        </p>
        <span className="pill">
          {practice ? t('Practice', '練習') : t('First attempt', '初挑戦')}
        </span>
      </div>
      <h1>
        {phase === 'ready' || phase === 'loading'
          ? t('Listen first. Then trust your eyes.', 'まず聞く。そして、絵を見抜く。')
          : phase === 'listening'
            ? t('Four clues. Keep them in mind.', '4つの英文を、覚えておこう。')
            : phase === 'drawing'
              ? t('Know it? Make your move.', 'わかった？ いまが押すとき。')
              : phase === 'paused'
                ? t('Take your time. We paused.', '離れている間に一時停止しました。')
                : result?.correct
                  ? t('Your ears caught it.', '聞き取れた。その一瞬！')
                  : result?.letter === null
                    ? t('A little too late. Try another?', '時間切れ。もう一問いこう。')
                    : t('Close. Here’s the difference.', '惜しい。決め手を見てみよう。')}
      </h1>

      {(phase === 'ready' || phase === 'loading') && (
        <section className="solo-intro card">
          <div className="intro-art" aria-hidden="true">
            <span>♪</span>
            <span>✎</span>
            <span>
              A B<br />C D
            </span>
          </div>
          <ol className="solo-rules">
            <li>
              {t(
                'Listen to four English sentences. The words stay hidden.',
                'A〜D の英文を聞く。文字は伏せられています。',
              )}
            </li>
            <li>
              {t(
                'Watch a sketch appear. Which sentence matches?',
                '少しずつ現れる絵は、どの英文の場面？',
              )}
            </li>
            <li>
              {t(
                'Tap A–D once. An earlier correct answer scores more.',
                'A〜D を一度だけ押す。早い正解ほど高得点。',
              )}
            </li>
          </ol>
          {firstResult && (
            <p className="note">
              {t(
                `Your first result: ${firstResult.points > 0 ? '+' : ''}${firstResult.points} points${firstResult.pct !== null ? ` at ${firstResult.pct}%` : ''}. This replay is practice.`,
                `初回の記録: ${firstResult.points > 0 ? '+' : ''}${firstResult.points}点${firstResult.pct !== null ? `（完成率 ${firstResult.pct}%）` : ''}。今回は練習として遊べます。`,
              )}
            </p>
          )}
          <div className="score-legend">
            <span>
              <b>300</b> {t('early', '早め')}
            </span>
            <span>
              <b>200</b> {t('middle', '途中')}
            </span>
            <span>
              <b>100</b> {t('late', '終盤')}
            </span>
            <span>
              <b>−100</b> {t('wrong', '誤答')}
            </span>
          </div>
          <div className="intro-actions">
            <button
              className="btn btn--primary btn--big"
              onClick={() => void start()}
              disabled={phase === 'loading'}
            >
              {phase === 'loading'
                ? t('Getting your audio ready…', '音声を準備しています…')
                : t('Start listening →', '聞いてスタート →')}
            </button>
            <button
              className="btn btn--ghost"
              onClick={() => void soundCheck()}
              disabled={testing || phase === 'loading'}
            >
              {testing
                ? t('Playing sound…', 'テスト再生中…')
                : soundReady
                  ? t('✓ Sound ready · test again', '✓ 音声OK · 再テスト')
                  : t('♫ Test my sound', '♫ 音声をテスト')}
            </button>
          </div>
          <AudioSpeed
            value={audioSpeed}
            onChange={changeAudioSpeed}
            disabled={phase === 'loading'}
          />
          <p className="muted small">
            {t('Sound on · About a minute · No sign-up', '音声あり · 約1分 · 登録なし')}
            {daily ? t(' · A new challenge at 00:00 UTC', ' · 毎日9時（日本時間）に更新') : ''}
          </p>
        </section>
      )}

      {phase !== 'ready' && phase !== 'loading' && (
        <div className="solo-game">
          <section
            className="solo-board"
            aria-label={t('Drawing and round progress', '線画と進行')}
          >
            <Canvas round={round} phase={canvasPhase} localClock />
            {phase === 'listening' && (
              <div className="listening-overlay" aria-live="polite">
                <div className="sound-bars" aria-hidden="true">
                  <i />
                  <i />
                  <i />
                  <i />
                  <i />
                </div>
                <strong>
                  {playing === null
                    ? t('Get ready…', 'まもなく…')
                    : `${t('Listening to', '聞き取り中')} ${LETTERS[playing]}`}
                </strong>
                <span>{t('The picture comes next.', '絵は、このあと。')}</span>
              </div>
            )}
            {phase === 'drawing' && (
              <div className="solo-progress">
                <span>
                  {pct}% {t('complete', '完成')}
                </span>
                <strong>
                  +{pointsForCorrect(pct)} {t('for a correct answer', '正解なら')}
                </strong>
                <div className="bar">
                  <i style={{ width: `${pct}%` }} />
                </div>
              </div>
            )}
          </section>
          <section className="solo-answers" aria-label={t('Your answer', 'あなたの回答')}>
            <p className="eyebrow">
              {phase === 'listening'
                ? t('Listen. Don’t read.', '読むより、耳で。')
                : phase === 'reveal'
                  ? t('The reveal', '答え合わせ')
                  : t('Choose once · A / B / C / D', '一度だけ回答 · A / B / C / D')}
            </p>
            <Choices
              round={round}
              playing={playing}
              onPick={finish}
              pickable={phase === 'drawing'}
              picked={result?.letter ?? null}
              myLetter={result?.letter ?? null}
            />
            {phase === 'listening' && (
              <p className="muted small">
                {t('Buttons unlock when the drawing starts.', '描画が始まると回答できます。')}
              </p>
            )}
            {phase === 'drawing' && (
              <p className="muted small">
                {t(
                  'Tap a letter, or use A–D on your keyboard. A wrong guess costs 100 points.',
                  'ボタン、またはキーボードの A〜D で回答。誤答は −100 点です。',
                )}
              </p>
            )}
            {phase === 'paused' && (
              <div className="card">
                <p>
                  {t(
                    'You left the tab during the round. Restart with the same clues as a practice attempt.',
                    '途中でタブを離れたため停止しました。同じ問題を練習としてやり直せます。',
                  )}
                </p>
                <button
                  className="btn btn--primary"
                  onClick={() => {
                    setPhase('ready');
                    setRound(null);
                  }}
                >
                  {t('Restart this round', 'この問題をやり直す')}
                </button>
              </div>
            )}
            {phase === 'reveal' && result && (
              <div className="solo-result" role="status">
                <strong className={`result-points${result.correct ? ' is-correct' : ''}`}>
                  {result.points > 0 ? '+' : ''}
                  {result.points}
                  <small>{t('points', '点')}</small>
                </strong>
                <p>
                  {result.pct !== null
                    ? t(
                        `You chose ${LETTERS[result.letter!]} at ${result.pct}% complete.`,
                        `完成率 ${result.pct}% で ${LETTERS[result.letter!]} を選択。`,
                      )
                    : t(
                        'No answer before the drawing finished.',
                        '描画が終わるまでに回答がありませんでした。',
                      )}
                </p>
                <p>
                  <b>{t('Listen for:', '決め手:')}</b> {round?.reveal?.keyWords.join(' / ')}
                </p>
                <button
                  className="btn btn--ghost"
                  onClick={() => {
                    soundTest.current?.abort();
                    const controller = new AbortController();
                    soundTest.current = controller;
                    setError('');
                    void unlockAudio()
                      .then(() =>
                        playRecording(
                          challenge.texts[challenge.correctLetterIndex]!,
                          audioSpeed,
                          controller.signal,
                        ),
                      )
                      .catch(() => {
                        if (!controller.signal.aborted)
                          setError(
                            t('Please try the sound again.', 'もう一度音声をお試しください。'),
                          );
                      });
                  }}
                >
                  {t('♫ Hear the correct sentence', '♫ 正解の英文を聞く')}
                </button>
                <AudioSpeed value={audioSpeed} onChange={changeAudioSpeed} />
              </div>
            )}
          </section>
        </div>
      )}
      {error && (
        <p className="error audio-error" role="alert">
          {error}
        </p>
      )}
      {phase === 'reveal' && result && (
        <Explanation text={challenge.texts[challenge.correctLetterIndex]!} />
      )}
      {phase === 'reveal' && result && (
        <section className="solo-after">
          <ShareResult challenge={id} {...result} practice={practice} />
          <div className="row">
            <button className="btn" onClick={onNext}>
              {t('Another round →', 'もう一問 →')}
            </button>
            <Link className="btn btn--ghost" to="/#host">
              {t('Host a game with friends', '友だちと遊ぶ部屋を作る')}
            </Link>
          </div>
          {daily && (
            <p className="muted small">
              {t(
                'Come back tomorrow for a new daily challenge. Replays are practice.',
                '明日はまた新しい一問。再挑戦は練習として遊べます。',
              )}
            </p>
          )}
        </section>
      )}
      <p className="solo-exit">
        {(oddMode || legacy) && (
          <Link to="/solo">← {t('Back to the regular 100 questions', '通常の100問へ戻る')}</Link>
        )}
        {!oddMode && !legacy && <Link to="/">← {t('Back to home', 'トップへ')}</Link>}
      </p>
    </main>
  );
}
